// src/pages/Matriculas/MisMatriculas.jsx
import React, { useEffect, useState } from "react";
import {
    Card, Button, Space, Typography, Tag,
    Descriptions, Divider, Modal, Alert, Progress
} from "antd";
import {
    IdcardOutlined, CheckCircleOutlined, ClockCircleOutlined,
    EnvironmentOutlined, DollarOutlined, EyeOutlined,
    WarningOutlined, CalendarOutlined,
    ShoppingCartOutlined, CloseCircleOutlined
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowRight, FiInfo } from "react-icons/fi";
import EncabezadoPagina from "../../../Ui/Cliente/EncabezadoPagina";
import EstadoVacio, { claseBotonPrimario, claseBotonSecundario } from "../../../Ui/Cliente/EstadoVacio";
import dayjs from "dayjs";
import "dayjs/locale/es";
import MatriculaService from "../../../../service/Matriculas";
import Auth from "../../../../service/Auth";
import { useNotify } from "../../../../providers/NotificationProvider";

dayjs.locale("es");

const { Title, Text } = Typography;

export default function MisMatriculas() {
    const navigate = useNavigate();
    const notify   = useNotify();

    const [loading,              setLoading]              = useState(true);
    const [matriculas,           setMatriculas]           = useState([]);
    // Recintos cuya matrícula está HOY a la venta (configuración activa). Si un
    // recinto no está acá, no se ofrece comprar ni renovar: el flujo de compra
    // terminaría en "No hay matrículas disponibles".
    const [recintosEnVenta,      setRecintosEnVenta]      = useState([]);
    const [selectedMatricula,    setSelectedMatricula]    = useState(null);
    const [detalleModalVisible,  setDetalleModalVisible]  = useState(false);
    const [user,                 setUser]                 = useState(null);

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const userData = await Auth.Me();
            setUser({
                sub:   userData.id   || userData.sub,
                name:  userData.name || userData.dynamoProfile?.name,
                email: userData.email,
                rut:   userData.rut  || userData.dynamoProfile?.rut,
            });
            setMatriculas((await MatriculaService.ObtenerMisMatriculas(userData.id || userData.sub)) || []);

            try {
                const disponibles = await MatriculaService.ObtenerRecintosConMatricula();
                setRecintosEnVenta(disponibles.map(r => String(r._id)));
            } catch (error) {
                // No poder resolver la disponibilidad no debe romper la pantalla:
                // se asume que no hay matrículas a la venta y no se ofrece comprar.
                console.error('Error verificando matrículas disponibles:', error);
                setRecintosEnVenta([]);
            }
        } catch (error) {
            console.error('Error:', error);
            notify.error("No se pudieron cargar las matrículas");
        } finally {
            setLoading(false);
        }
    };

    const handleVerDetalle = (m) => { setSelectedMatricula(m); setDetalleModalVisible(true); };

    const esMatriculaVigente = (m) => {
        if (m.estado !== 'activa') return false;
        if (!m.fechaVencimiento || m.tipoVigencia === 'indefinida') return true;
        const hoy = dayjs();
        const ven = dayjs(m.fechaVencimiento);
        return hoy.isBefore(ven) || hoy.isSame(ven, 'day');
    };

    const calcularDiasRestantes = (fechaVencimiento, tipoVigencia) => {
        if (tipoVigencia === 'indefinida' || !fechaVencimiento) return null;
        const hoy = dayjs();
        const ven = dayjs(fechaVencimiento);
        if (hoy.isAfter(ven)) return 0;
        return ven.diff(hoy, 'day');
    };

    const getEstadoTag = (m) => {
        const vigente = esMatriculaVigente(m);
        if (!vigente) return <Tag color="red"    icon={<CloseCircleOutlined />}>Expirada</Tag>;
        const dias = calcularDiasRestantes(m.fechaVencimiento, m.tipoVigencia);
        if (dias === null)    return <Tag color="blue"   icon={<CheckCircleOutlined />}>Indefinida</Tag>;
        if (dias <= 7)        return <Tag color="orange" icon={<WarningOutlined />}>Por vencer</Tag>;
        return                       <Tag color="blue"  icon={<CheckCircleOutlined />}>Vigente</Tag>;
    };

    const getDiasInfo = (m) => {
        if (!m.fechaVencimiento || m.tipoVigencia === 'indefinida') return { text: 'Sin vencimiento', color: '#0047bb', pct: null };
        const dias = calcularDiasRestantes(m.fechaVencimiento, m.tipoVigencia);
        if (dias === 0) return { text: 'Expirada', color: '#ff4d4f', pct: 0 };
        // Calcular porcentaje: necesitamos la fecha de inicio
        const inicio = m.fechaInicio || m.createdAt;
        let pct = null;
        if (inicio) {
            const total = dayjs(m.fechaVencimiento).diff(dayjs(inicio), 'day');
            if (total > 0) pct = Math.max(0, Math.min(100, Math.round((dias / total) * 100)));
        }
        if (dias <= 7)  return { text: `${dias} día${dias === 1 ? '' : 's'}`,  color: '#ff4d4f', pct };
        if (dias <= 30) return { text: `${dias} días`,                           color: '#fa8c16', pct };
        return               { text: `${dias} días`,                             color: '#0047bb', pct };
    };

    const getRecintoInfo = (r) => !r ? 'Recinto no especificado' : typeof r === 'string' ? r : r.nombre || 'Recinto sin nombre';

    const hayMatriculasEnVenta = recintosEnVenta.length > 0;
    const seVendeMatricula = (m) => recintosEnVenta.includes(String(m.recinto?._id || m.recinto));

    // Estadísticas
    const vigentes    = matriculas.filter(m =>  esMatriculaVigente(m)).length;
    const expiradas   = matriculas.filter(m => !esMatriculaVigente(m)).length;
    const porVencer   = matriculas.filter(m => {
        if (!esMatriculaVigente(m) || !m.fechaVencimiento) return false;
        const d = calcularDiasRestantes(m.fechaVencimiento, m.tipoVigencia);
        return d !== null && d > 0 && d <= 30;
    }).length;

    return (
        <div className="min-h-screen">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10">

                {/* ── Header (con el botón comprar, solo si hay matrícula a la venta) ── */}
                <EncabezadoPagina
                    icon={<IdcardOutlined />}
                    titulo="Mis"
                    destacado="matrículas"
                    subtitulo="Estado y vencimiento de tus matrículas de recinto"
                >
                    {!loading && hayMatriculasEnVenta && matriculas.length > 0 && (
                        <button type="button" onClick={() => navigate('/matriculas/comprar')} className={claseBotonPrimario}>
                            <ShoppingCartOutlined /> Comprar nueva matrícula
                        </button>
                    )}
                </EncabezadoPagina>

                {/* ── Stat row ─────────────────────────────────────────────── */}
                {!loading && matriculas.length > 0 && (
                    <div className="grid grid-cols-3 gap-3 mb-6">
                        {[
                            { label: 'Vigentes',   value: vigentes,  cls: 'bg-white border-blue-100 text-primary' },
                            { label: 'Por vencer', value: porVencer, cls: 'bg-[#fffbea] border-[#fde68a] text-[#92400e]' },
                            { label: 'Expiradas',  value: expiradas, cls: 'bg-white border-red-100 text-red-600' },
                        ].map(s => (
                            <div key={s.label} className={`rounded-2xl border px-4 py-4 text-center ${s.cls}`}>
                                <div className="text-3xl font-extrabold leading-none">{s.value}</div>
                                <div className="text-xs font-semibold text-gray-500 mt-1.5">{s.label}</div>
                            </div>
                        ))}
                    </div>
                )}

                {/* ── Alerta por vencer ─────────────────────────────────────── */}
                {porVencer > 0 && (
                    <Alert
                        message={`Tienes ${porVencer} matrícula${porVencer !== 1 ? 's' : ''} por vencer en los próximos 30 días`}
                        description="Renuévalas para mantener acceso a talleres exclusivos."
                        type="warning" showIcon icon={<WarningOutlined />}
                        style={{ marginBottom: 20, borderRadius: 10 }}
                    />
                )}

                {/* ── Loading ───────────────────────────────────────────────── */}
                {loading && (
                    <div className="flex flex-col gap-4" aria-busy="true" aria-label="Cargando matrículas">
                        {[0, 1].map(i => (
                            <div key={i} className="rounded-2xl bg-white p-6 animate-pulse">
                                <div className="h-5 bg-gray-200 rounded-full w-1/3 mb-3" />
                                <div className="h-3 bg-gray-100 rounded-full w-1/2 mb-6" />
                                <div className="h-3 bg-gray-100 rounded-full w-2/3" />
                            </div>
                        ))}
                    </div>
                )}

                {/* ── Empty ─────────────────────────────────────────────────── */}
                {!loading && matriculas.length === 0 && (
                    <EstadoVacio
                        icon={<IdcardOutlined />}
                        titulo="No tienes matrículas"
                        texto="Algunos recintos piden una matrícula para inscribirte en sus talleres. Cuando tengas una, aquí verás su estado y cuándo vence."
                        acciones={<>
                            {hayMatriculasEnVenta && (
                                <button type="button" onClick={() => navigate('/matriculas/comprar')} className={claseBotonPrimario}>
                                    <ShoppingCartOutlined /> Comprar matrícula
                                </button>
                            )}
                            <Link to="/#talleres-section" className={hayMatriculasEnVenta ? claseBotonSecundario : claseBotonPrimario}>
                                Ver talleres <FiArrowRight size={15} />
                            </Link>
                        </>}
                    >
                        <div className="mt-10 pt-8 border-t border-gray-100 w-full max-w-md">
                            <div className="flex items-start gap-3 p-4 rounded-2xl bg-[#fffbea] border border-[#fde68a] text-left">
                                <span className="w-9 h-9 flex-shrink-0 rounded-xl bg-secondary text-[#00266b] flex items-center justify-center">
                                    <FiInfo size={17} />
                                </span>
                                <p className="text-sm text-[#5c4300] leading-relaxed m-0">
                                    ¿No sabes si la necesitas? Si un taller la requiere, te avisamos en el carrito
                                    y puedes comprarla ahí mismo.
                                </p>
                            </div>
                        </div>
                    </EstadoVacio>
                )}

                {/* ── Lista de matrículas ───────────────────────────────────── */}
                {!loading && matriculas.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {matriculas.map((m) => {
                            const vigente     = esMatriculaVigente(m);
                            const diasInfo    = getDiasInfo(m);
                            const recintoInfo = getRecintoInfo(m.recinto);
                            const diasRest    = calcularDiasRestantes(m.fechaVencimiento, m.tipoVigencia);

                            const borderColor = !vigente ? '#ff4d4f'
                                : (diasRest !== null && diasRest <= 7) ? '#fa8c16'
                                : '#0047bb';

                            return (
                                <Card
                                    key={m._id}
                                    style={{ borderRadius: 16, borderLeft: `5px solid ${borderColor}` }}
                                    styles={{ body: { padding: '20px 24px' } }}
                                >
                                    {/* Encabezado */}
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                                            <div style={{ width: 44, height: 44, borderRadius: 10, background: vigente ? '#0047bb' : '#d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 20, flexShrink: 0 }}>
                                                <IdcardOutlined />
                                            </div>
                                            <div>
                                                <Title level={4} style={{ margin: '0 0 5px' }}>
                                                    {recintoInfo}
                                                </Title>
                                                <Space wrap size={4}>
                                                    {getEstadoTag(m)}
                                                    {m.tipoVigencia === 'anual'      && <Tag color="purple">Anual</Tag>}
                                                    {m.tipoVigencia === 'indefinida' && <Tag color="cyan">Indefinida</Tag>}
                                                </Space>
                                            </div>
                                        </div>
                                        <Space size="small" style={{ flexShrink: 0 }}>
                                            <Button size="small" icon={<EyeOutlined />} onClick={() => handleVerDetalle(m)}>
                                                Ver detalle
                                            </Button>
                                            {!vigente && seVendeMatricula(m) && (
                                                <Button size="small" type="primary" icon={<ShoppingCartOutlined />}
                                                    onClick={() => navigate(`/matriculas/comprar?recinto=${m.recinto._id || m.recinto}`)}
                                                    style={{ background: '#fa8c16', borderColor: '#fa8c16' }}
                                                >
                                                    Renovar
                                                </Button>
                                            )}
                                        </Space>
                                    </div>

                                    <Divider style={{ margin: '0 0 16px' }} />

                                    {/* Info + Countdown */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'start' }}>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 32px' }}>
                                            <div>
                                                <Text type="secondary" style={{ fontSize: 11 }}><CalendarOutlined style={{ marginRight: 4 }} />Compra</Text>
                                                <Text style={{ display: 'block', fontSize: 13 }}>
                                                    {dayjs(m.fechaInicio || m.createdAt).format('DD/MM/YYYY')}
                                                </Text>
                                            </div>
                                            <div>
                                                <Text type="secondary" style={{ fontSize: 11 }}><ClockCircleOutlined style={{ marginRight: 4 }} />Vencimiento</Text>
                                                <Text style={{ display: 'block', fontSize: 13 }}>
                                                    {m.fechaVencimiento ? dayjs(m.fechaVencimiento).format('DD/MM/YYYY') : 'Sin vencimiento'}
                                                </Text>
                                            </div>
                                            {m.recinto?.direccion && (
                                                <div>
                                                    <Text type="secondary" style={{ fontSize: 11 }}><EnvironmentOutlined style={{ marginRight: 4 }} />Dirección</Text>
                                                    <Text style={{ display: 'block', fontSize: 13 }}>{m.recinto.direccion}</Text>
                                                </div>
                                            )}
                                            <div>
                                                <Text type="secondary" style={{ fontSize: 11 }}><DollarOutlined style={{ marginRight: 4 }} />Pagado</Text>
                                                <Text strong style={{ display: 'block', fontSize: 13, color: '#0047bb' }}>
                                                    ${(m.valorPagado || m.monto || 0).toLocaleString('es-CL')}
                                                </Text>
                                            </div>
                                        </div>

                                        {/* Días restantes */}
                                        {m.fechaVencimiento && m.tipoVigencia !== 'indefinida' && (
                                            <div style={{ padding: '10px 16px', background: '#f9fafb', borderRadius: 10, border: '1px solid #f0f0f0', textAlign: 'center', minWidth: 100 }}>
                                                <div style={{ fontSize: 26, fontWeight: 800, color: diasInfo.color, lineHeight: 1 }}>
                                                    {diasRest}
                                                </div>
                                                <Text type="secondary" style={{ fontSize: 11 }}>días restantes</Text>
                                                {diasInfo.pct !== null && (
                                                    <Progress
                                                        percent={diasInfo.pct}
                                                        size={[80, 5]}
                                                        showInfo={false}
                                                        strokeColor={diasInfo.color}
                                                        style={{ margin: '6px 0 0', width: '100%' }}
                                                    />
                                                )}
                                            </div>
                                        )}
                                        {m.tipoVigencia === 'indefinida' && (
                                            <div style={{ padding: '10px 14px', background: '#eff6ff', borderRadius: 10, border: '1px solid #bfdbfe', textAlign: 'center' }}>
                                                <div style={{ fontSize: 22, color: '#0047bb' }}>∞</div>
                                                <Text style={{ fontSize: 11, color: '#0047bb' }}>Permanente</Text>
                                            </div>
                                        )}
                                    </div>

                                    {/* Alerta por vencer inline */}
                                    {vigente && m.fechaVencimiento && diasRest !== null && diasRest <= 30 && (
                                        <Alert
                                            message={`Tu matrícula vence en ${diasRest} día${diasRest !== 1 ? 's' : ''}`}
                                            description="Renueva pronto para mantener tu acceso"
                                            type={diasRest <= 7 ? 'error' : 'warning'} showIcon
                                            style={{ marginTop: 14, borderRadius: 8 }}
                                            action={seVendeMatricula(m) ? (
                                                <Button size="small" onClick={() => navigate(`/matriculas/comprar?recinto=${m.recinto._id || m.recinto}`)}>
                                                    Renovar
                                                </Button>
                                            ) : null}
                                        />
                                    )}
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* ── Modal detalle ─────────────────────────────────────────── */}
                <Modal
                    title={<Space><IdcardOutlined style={{ color: '#0047bb' }} /><span>Detalle de Matrícula</span></Space>}
                    open={detalleModalVisible}
                    onCancel={() => setDetalleModalVisible(false)}
                    footer={<Button type="primary" onClick={() => setDetalleModalVisible(false)} style={{ background: '#0047bb', borderColor: '#0047bb' }}>Cerrar</Button>}
                    width={640}
                    styles={{ body: { padding: '16px 24px' } }}
                >
                    {selectedMatricula && (() => {
                        const vigente  = esMatriculaVigente(selectedMatricula);
                        const diasInfo = getDiasInfo(selectedMatricula);
                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {/* Estado */}
                                <div style={{ textAlign: 'center', padding: '16px', background: '#f9fafb', borderRadius: 10 }}>
                                    {getEstadoTag(selectedMatricula)}
                                    <div style={{ marginTop: 8 }}>
                                        <Text strong style={{ color: diasInfo.color }}>{diasInfo.text}</Text>
                                        {selectedMatricula.tipoVigencia === 'indefinida' && (
                                            <Text strong style={{ color: '#0047bb' }}> ✨ Vigencia permanente</Text>
                                        )}
                                    </div>
                                </div>

                                <Descriptions title="Beneficiario" column={1} bordered size="small">
                                    <Descriptions.Item label="Nombre">{selectedMatricula.datosInscrito?.nombre || user?.name}</Descriptions.Item>
                                    <Descriptions.Item label="RUT">{selectedMatricula.rut || user?.rut}</Descriptions.Item>
                                    <Descriptions.Item label="Email">{selectedMatricula.datosInscrito?.email || user?.email}</Descriptions.Item>
                                </Descriptions>

                                <Descriptions title="Matrícula" column={1} bordered size="small">
                                    <Descriptions.Item label="Recinto">
                                        <div>
                                            <Text strong>{getRecintoInfo(selectedMatricula.recinto)}</Text>
                                            {selectedMatricula.recinto?.direccion && (
                                                <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{selectedMatricula.recinto.direccion}</Text>
                                            )}
                                        </div>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Tipo de vigencia">
                                        {selectedMatricula.tipoVigencia === 'anual' ? 'Anual (1 año)' :
                                         selectedMatricula.tipoVigencia === 'indefinida' ? 'Indefinida (Permanente)' : 'Otro'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Fecha de compra">
                                        {dayjs(selectedMatricula.fechaInicio || selectedMatricula.createdAt).format('DD/MM/YYYY HH:mm')}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Vencimiento">
                                        {selectedMatricula.fechaVencimiento ? dayjs(selectedMatricula.fechaVencimiento).format('DD/MM/YYYY') : 'Sin vencimiento'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Estado">{getEstadoTag(selectedMatricula)}</Descriptions.Item>
                                </Descriptions>

                                <Descriptions title="Pago" column={1} bordered size="small">
                                    <Descriptions.Item label="Monto pagado">
                                        <Text strong style={{ color: '#0047bb', fontSize: 16 }}>
                                            ${(selectedMatricula.valorPagado || selectedMatricula.monto || 0).toLocaleString('es-CL')}
                                        </Text>
                                    </Descriptions.Item>
                                    {selectedMatricula.metodoPago && <Descriptions.Item label="Método de pago">{selectedMatricula.metodoPago}</Descriptions.Item>}
                                    {selectedMatricula.ordenCompra  && <Descriptions.Item label="Orden de compra">{selectedMatricula.ordenCompra}</Descriptions.Item>}
                                </Descriptions>

                                {!vigente && (
                                    <Alert
                                        message="Matrícula Expirada"
                                        description={seVendeMatricula(selectedMatricula)
                                            ? "Puedes renovarla para recuperar el acceso a talleres exclusivos."
                                            : "La matrícula de este recinto no está disponible para compra en este momento."}
                                        type="error" showIcon
                                        action={seVendeMatricula(selectedMatricula) ? (
                                            <Button type="primary" size="small" danger
                                                onClick={() => { setDetalleModalVisible(false); navigate(`/matriculas/comprar?recinto=${selectedMatricula.recinto._id || selectedMatricula.recinto}`); }}
                                            >
                                                Renovar ahora
                                            </Button>
                                        ) : null}
                                    />
                                )}
                            </div>
                        );
                    })()}
                </Modal>
            </div>
        </div>
    );
}
